import React from 'react';
import { Code, ShoppingCart, Search, Wrench, ArrowRight, Check, Camera, Video, Plane } from 'lucide-react';
import { mockData } from '../data/mock';

const Services = () => {
  const { services, otherServices } = mockData;

  const serviceIcons = {
    1: Code,
    2: ShoppingCart,
    3: Search,
    4: Wrench,
    5: Video,
    6: Camera,
    7: Plane
  };

  return (
    <section
      id="services"
      className="py-20"
      style={{
        backgroundColor: '#F9FAFB',
        backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)',
        backgroundSize: '4px 4px'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6 tracking-tight">
            Mes Services
          </h2>
          <div className="w-16 h-1 rounded-full mx-auto mb-8" style={{ backgroundColor: '#f97316' }}></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Développement web & e-commerce en priorité, avec une offre vidéo solide pour
            renforcer votre communication.
          </p>
        </div>

        {/* Main Web Services */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-black mb-8 text-center">Développement Web & E-commerce</h3>
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {services.map((service, index) => {
              const Icon = serviceIcons[service.id];
              return (
                <div 
                  key={service.id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden hover:-translate-y-2"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    
                    {/* Icon */}
                    <div className="absolute top-6 left-6">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center shadow-lg" style={{ backgroundColor: '#f97316' }}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 
                      className="text-2xl font-bold text-gray-900 mb-4 transition-colors"
                      onMouseEnter={(e) => e.target.style.color = '#f97316'}
                      onMouseLeave={(e) => e.target.style.color = '#374151'}
                    >
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-3">
                          <Check className="h-5 w-5 flex-shrink-0" style={{ color: '#f97316' }} />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <button 
                      className="group/btn inline-flex items-center space-x-2 font-medium transition-colors"
                      style={{ color: '#f97316' }}
                      onMouseEnter={(e) => e.target.style.color = '#ea580c'}
                      onMouseLeave={(e) => e.target.style.color = '#f97316'}
                    >
                      <span>En savoir plus</span>
                      <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Other Services - Video */}
        <div className="mb-16">
          <h3 className="text-xl font-semibold text-gray-700 mb-4 text-center">Production Vidéo</h3>
          <div className="text-center mb-8">
            <button
              onClick={() => {
                window.location.href = '/realisations/video';
              }}
              className="inline-flex items-center space-x-3 text-white px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 active:scale-95 shadow-md"
              style={{ backgroundColor: '#f97316' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
            >
              <span>Voir les réalisations vidéo</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {otherServices.map((service) => {
              const Icon = serviceIcons[service.id];
              return (
                <div 
                  key={service.id}
                  className="group bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                    
                    {/* Icon */}
                    <div className="absolute top-4 left-4">
                      <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center shadow-md">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      {service.title}
                    </h4>
                    
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></div>
                          <span className="text-gray-600 text-xs">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500 italic">
              * Disponibles en complément de votre site ou en prestation dédiée
            </p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-black rounded-2xl p-8 lg:p-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              Projet sur mesure ?
            </h3>
            <p className="text-gray-300 mb-8 text-lg max-w-2xl mx-auto">
              Site, e-commerce ou vidéo : discutons ensemble de vos besoins
              pour créer une solution parfaitement adaptée.
            </p>
            <button 
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg"
              style={{ backgroundColor: '#f97316' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
            >
              Parlez-moi de votre projet
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
